import {
    CoralClient,
    CoralClientConstructor,
    CoralClientConfigFactory,
    CoralClientFactory,
    CoralFetchHandler,
    PartitionCoralClientConfigFactory,
    CoralInterceptor,
} from '@amzn/ihm-js-coral-client';
import { RegionMappingService } from '@src/services';
import { EnvironmentDetail } from '@src/utils';

import { IIBTypeScriptCoralHandler } from './IIBTypeScriptCoralHandler';
import { AFTXTypeScriptCoralHandler } from './AFTXTypeScriptCoralHandler';
import { ServiceCallMetricsInterceptor } from './ServiceCallMetricsInterceptor';

export const getQualifier = (): string => {
    const domain = EnvironmentDetail.getDomain();
    const region = EnvironmentDetail.getRegion();
    const realm = RegionMappingService.getAwsRegionFromAirportCode(region);
    return `${domain}.${realm}`;
};

interface ICreateTypeScriptClientProps<T extends CoralClient> {
    configFactory: CoralClientConfigFactory;
    clientConstructor: CoralClientConstructor<T>;
    sentryFetch: typeof fetch;
    clientQualifier?: string;
    interceptors?: CoralInterceptor[];
}

/**
 * Creates a typescript client. Qualifier is an optional parameter which will default to domain.realm if not provided.
 * @param configFactory
 * @param clientConstructor
 * @param sentryFetch
 * @param clientQualifier
 */
export const createTypeScriptClient = <T extends CoralClient>({
    configFactory,
    clientConstructor,
    sentryFetch,
    clientQualifier,
    interceptors = [],
}: ICreateTypeScriptClientProps<T>): T => {
    const storeId = EnvironmentDetail.getStoreId() || 'partition';
    let coralHandler;
    if (EnvironmentDetail.isIIBAuthN()) {
        coralHandler = new IIBTypeScriptCoralHandler();
    } else if (EnvironmentDetail.isAFTXAuthN()) {
        coralHandler = new AFTXTypeScriptCoralHandler();
    } else {
        coralHandler = new CoralFetchHandler(sentryFetch);
    }

    return new CoralClientFactory(
        [new PartitionCoralClientConfigFactory(storeId.toLowerCase().replace('_', '-'), configFactory)],
        clientQualifier || getQualifier(),
        coralHandler,
        [new ServiceCallMetricsInterceptor(), ...interceptors],
    ).get(clientConstructor);
};
